//© 2025 Kaitlyn Troxell
import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc, 
  serverTimestamp, 
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export function useCalendarEvents(user, currentProjectId, contractors, tasks) {
  const [events, setEvents] = useState([]);
  
  // Extract fetch logic into a reusable function
  const fetchCalendarEvents = useCallback(async () => {
    if (!user || !currentProjectId) {
      setEvents([]);
      return;
    }

    try {
      const eventsQuery = query(
        collection(db, 'calendar_events'),
        where('project_id', '==', currentProjectId),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(eventsQuery);
      const calendarEvents = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        
        // Find the associated task to check completion status
        const task = tasks.find(t => t.id === data.task_id);
        const isCompleted = task?.completed || false;
        const hasContractor = data.contractor_id !== null;
        const isOutOfOffice = data.title.toLowerCase().includes('out of office');
        
        // Determine color based on completion, contractor assignment, and out of office
        let backgroundColor = data.backgroundColor;
        let borderColor = data.borderColor;
        
        if (isOutOfOffice) {
          backgroundColor = '#dc3545'; // Red for Out of Office
          borderColor = '#dc3545';
        } else if (isCompleted) {
          backgroundColor = '#28a745'; // Green for completed
          borderColor = '#28a745';
        } else if (hasContractor) {
          backgroundColor = '#ffc107'; // Yellow for assigned but not completed
          borderColor = '#ffc107';
        } else {
          backgroundColor = '#4285f4'; // Blue for unassigned
          borderColor = '#4285f4';
        }

        calendarEvents.push({
          id: docSnap.id,
          title: data.title,
          start: data.start.toDate(),
          end: data.end ? data.end.toDate() : null,
          allDay: data.allDay,
          backgroundColor,
          borderColor,
          task_id: data.task_id,
          contractor_id: data.contractor_id,
          notes: data.notes || ''
        });
      });

      setEvents(calendarEvents);
    } catch (err) {
      console.error("Error fetching calendar events:", err);
    }
  }, [user, currentProjectId, tasks]);

  // Fetch calendar events from Firestore
  useEffect(() => {
    fetchCalendarEvents();
  }, [fetchCalendarEvents]);

  // Refresh function that can be called manually
  const refreshEvents = useCallback(() => {
    fetchCalendarEvents();
  }, [fetchCalendarEvents]);

  // Save calendar event
  const saveCalendarEvent = async (event) => {
    try {
      const eventData = {
        title: event.title,
        start: Timestamp.fromDate(event.start),
        allDay: event.allDay,
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        task_id: event.task_id || null,
        contractor_id: event.contractor_id || null,
        project_id: currentProjectId,
        created_at: serverTimestamp()
      };

      if (event.end) {
        eventData.end = Timestamp.fromDate(event.end);
      }

      const docRef = await addDoc(collection(db, 'calendar_events'), eventData);

      setEvents(prev => [...prev, { ...event, id: docRef.id }]);
    } catch (error) {
      console.error("Error saving calendar event:", error);
    }
  };

  // Update notes
  const updateCalendarEventNotes = async (eventId, notes) => {
    try {
      const eventRef = doc(db, 'calendar_events', eventId);
      await updateDoc(eventRef, { notes });

      setEvents(prev => prev.map(event =>
        event.id === eventId ? { ...event, notes } : event
      ));
    } catch (error) {
      console.error("Error updating event notes:", error);
      alert("Failed to update notes");
    }
  };

  // Update end date
  const updateCalendarEventEndDate = async (eventId, endDate) => {
    try {
      const eventRef = doc(db, 'calendar_events', eventId);
      
      // Convert Date object to Timestamp for Firestore
      const endTimestamp = endDate instanceof Date ? Timestamp.fromDate(endDate) : null;
      
      await updateDoc(eventRef, { 
        end: endTimestamp 
      });

      // Update local state with Date object
      setEvents(prev => prev.map(event =>
        event.id === eventId ? { ...event, end: endDate } : event
      ));
    } catch (error) {
      console.error("Error updating event end date:", error);
      alert("Failed to update end date");
    }
  };

  // Update calendar event (for resize/move)
  const updateCalendarEvent = async (eventId, updates) => {
    try {
      const eventRef = doc(db, 'calendar_events', eventId);
      
      // Convert Date objects to Timestamps for Firestore
      const firestoreUpdates = { ...updates };
      if (updates.start instanceof Date) {
        firestoreUpdates.start = Timestamp.fromDate(updates.start);
      }
      if (updates.end instanceof Date) {
        firestoreUpdates.end = Timestamp.fromDate(updates.end);
      }
      
      await updateDoc(eventRef, firestoreUpdates);

      // Update local state with Date objects
      setEvents(prev => prev.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      ));
    } catch (error) {
      console.error("Error updating calendar event:", error);
      throw error;
    }
  };

  // Delete event
  const deleteCalendarEventById = async (eventId) => {
    try {
      await deleteDoc(doc(db, 'calendar_events', eventId));
      setEvents(prev => prev.filter(event => event.id !== eventId));
    } catch (error) {
      console.error("Error deleting calendar event:", error);
      alert("Failed to delete event");
    }
  };

  // Update contractor assignments
  const updateCalendarEventsForTask = async (taskId, newContractorId, taskTitle) => {
    try {
      const taskEvents = events.filter(event => event.task_id === taskId);

      for (const event of taskEvents) {
        const contractor = newContractorId ? contractors.find(c => c.id === newContractorId) : null;

        const updatedTitle = contractor ? `${taskTitle} (${contractor.name})` : taskTitle;
        const updatedBackgroundColor = contractor ? '#ffc107' : '#4285f4'; // Yellow if contractor assigned
        const updatedBorderColor = contractor ? '#ffc107' : '#4285f4';

        const eventRef = doc(db, 'calendar_events', event.id);
        await updateDoc(eventRef, {
          title: updatedTitle,
          backgroundColor: updatedBackgroundColor,
          borderColor: updatedBorderColor,
          contractor_id: newContractorId
        });

        setEvents(prev => prev.map(e =>
          e.id === event.id
            ? {
                ...e,
                title: updatedTitle,
                backgroundColor: updatedBackgroundColor,
                borderColor: updatedBorderColor,
                contractor_id: newContractorId
              }
            : e
        ));
      }
    } catch (error) {
      console.error("Error updating calendar events:", error);
    }
  };

  // update color on event completion
  const updateEventColorsForCompletion = async (taskId, completed) => {
    try {
      const taskEvents = events.filter(event => event.task_id === taskId);

      for (const event of taskEvents) {
        const backgroundColor = completed ? '#28a745' : (event.contractor_id ? '#ffc107' : '#4285f4');
        const borderColor = completed ? '#28a745' : (event.contractor_id ? '#ffc107' : '#4285f4');

        const eventRef = doc(db, 'calendar_events', event.id);
        await updateDoc(eventRef, {
          backgroundColor,
          borderColor
        });

        setEvents(prev => prev.map(e =>
          e.id === event.id
            ? { ...e, backgroundColor, borderColor }
            : e
        ));
      }
    } catch (error) {
      console.error("Error updating event colors:", error);
    }
  };

  return { 
    events, 
    setEvents,
    saveCalendarEvent,
    updateCalendarEvent,
    updateCalendarEventNotes, 
    deleteCalendarEventById,
    updateCalendarEventsForTask,
    updateEventColorsForCompletion,
    updateCalendarEventEndDate,
    refreshEvents  // Export the refresh function
  };
}

export default useCalendarEvents;